import React, { useState, useCallback, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './contexts/AuthContext';
import { MasterDataProvider } from './contexts/MasterDataContext';
import { DateProvider } from './contexts/DateContext';
import { useMasterData } from './hooks/useMasterData';
import { isDemoMode } from './utils/environment';
import { Role } from './types';
import logger from './utils/logger';
import { Alert } from './components/ui';
import LoginPage from './pages/auth/LoginPage';
import DemoSelectionPage from './pages/auth/DemoSelectionPage';
import MasterLayout from './components/layout/MasterLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import { PartnerListPage } from './pages/master/PartnerListPage';
import { LandTransporterListPage } from './pages/master/LandTransporterListPage';
import { VehicleModelListPage } from './pages/master/VehicleModelListPage';
import { VehicleTypeListPage } from './pages/master/VehicleTypeListPage';
import CaseListPage from './pages/cases/CaseListPage';
import { CaseDetailPage } from './pages/cases/CaseDetailPage';
import { CaseFormPage } from './pages/cases/CaseFormPage';
import DriverCasesPage from './pages/driver/DriverCasesPage';
import UserProfilePage from './pages/profile/UserProfilePage';
import SchedulePage from './pages/schedule/SchedulePage';
import ScheduleRegistrationPage from './pages/schedule/ScheduleRegistrationPage';
import ReportPage from './pages/reports/ReportPage';
import SettingsPage from './pages/admin/SettingsPage';
import AccountPage from './pages/admin/AccountPage';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import CompanyManagement from './pages/admin/CompanyManagement';
import CompanyAdminRegistration from './pages/admin/CompanyAdminRegistration';
import CompanyAdminManagement from './pages/admin/CompanyAdminManagement';
import AllUsersManagement from './pages/admin/AllUsersManagement';
import UserDetailPage from './pages/admin/UserDetailPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import BackupStatusPage from './pages/admin/BackupStatusPage';
import BusinessPartnerMasterPage from './pages/admin/BusinessPartnerMasterPage';
import { CompanyUsersPage } from './pages/admin/CompanyUsersPage';
import DailyReportLogsPage from './pages/admin/DailyReportLogsPage';
import ScheduleManagementPage from './pages/admin/ScheduleManagementPage';
import { HeaderDemoNotice } from './components/common/DemoModeNotice';
import { DemoResetConfirmDialog } from './components/demo/DemoResetConfirmDialog';
import { demoResetService } from './services/demoResetService';
import { demoOverlayResetService } from './services/demoOverlayResetService';
import { SnapshotManager } from './utils/snapshotManager';

interface AlertState {
  show: boolean;
  variant: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
}

const ProtectedRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const { resetData } = useMasterData();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [alertState, setAlertState] = useState<AlertState>({
    show: false,
    variant: 'info',
    message: '',
  });

  const showAlert = useCallback(
    (variant: AlertState['variant'], message: string, title?: string) => {
      setAlertState({
        show: true,
        variant,
        message,
        title,
      });
    },
    []
  );

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, show: false }));
  }, []);

  // Demo company ID setup
  useEffect(() => {
    if (user?.companyId && isDemoMode()) {
      demoResetService.setCompanyId(user.companyId);
      demoOverlayResetService.setCompanyId(user.companyId);
    }
  }, [user?.companyId]);

  const handleConfirmReset = useCallback(async () => {
    try {
      logger.info('✅ ユーザーがリセットを承認');
      setShowResetConfirm(false);

      // 1. 新しいオーバーレイ方式によるリセット
      logger.info('🔄 オーバーレイ方式のデータリセットを実行');
      const resetResult = await demoOverlayResetService.resetDemoData({
        onProgress: (message, step, totalSteps) => {
          logger.info(`📊 リセット進捗 (${step}/${totalSteps}): ${message}`);
        },
        resetReason: 'デモ体験でのデータ初期化',
        triggeredBy: user?.name || 'user',
      });

      if (resetResult.success) {
        logger.info('✅ オーバーレイ方式のデータリセット完了', {
          generationBefore: resetResult.generationBefore,
          generationAfter: resetResult.generationAfter,
          executionTime: resetResult.executionTimeMs,
        });

        // 成功通知
        showAlert(
          'success',
          `データリセットが完了しました。\n世代: ${resetResult.generationBefore} → ${resetResult.generationAfter}\nページを更新します。`,
          '✅ リセット完了'
        );
        setTimeout(() => window.location.reload(), 2000);
        return;
      }

      // 2. フォールバック: 従来のスナップショット方式
      logger.warn('⚠️ オーバーレイ方式が失敗、フォールバック実行');
      if (await handleSnapshotReset()) {
        logger.info('🎯 フォールバック: スナップショット復元成功');
        return;
      }

      // 3. 最終フォールバック: レガシー方式
      logger.info('🔄 最終フォールバック: レガシー方式実行');
      if (!resetData) {
        throw new Error('すべてのリセット方式が利用できません');
      }

      await resetData();
      logger.info('✅ レガシー方式のデータリセット完了');

      // 成功通知
      showAlert(
        'success',
        'データリセットが完了しました。ページを更新してください。',
        '✅ リセット完了'
      );
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      logger.error('❌ データリセットエラー:', error);
      showAlert('error', 'データリセットに失敗しました。再度お試しください。', '❌ リセット失敗');
    }
  }, [resetData, user?.name, showAlert]);

  const handleSnapshotReset = useCallback(async (): Promise<boolean> => {
    try {
      logger.info('🔄 スナップショット復元を実行中...');

      await SnapshotManager.resetSnapshot();
      logger.info('✅ スナップショット復元成功');
      showAlert('success', 'デモデータリセットが完了しました。', '✅ リセット完了');
      // データ更新のための軽いリロード
      setTimeout(() => window.location.reload(), 2000);
      return true;
    } catch (error) {
      logger.error('❌ スナップショット復元エラー:', error);
      return false;
    }
  }, [showAlert]);

  const handleResetDemo = useCallback(() => {
    if (user?.role === Role.ADMIN) {
      setShowResetConfirm(true);
    }
  }, [user?.role]);

  // 認証状態の初期化中はローディング表示
  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-fadeIn'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-gray-600 dark:text-gray-300 text-sm'>認証情報を確認中です...</p>
          <p className='text-gray-400 dark:text-gray-500 text-xs mt-2'>数秒かかる場合があります</p>
        </div>
      </div>
    );
  }

  // 未認証の場合はログイン画面にリダイレクト
  if (!user) {
    return <Navigate to='/login' replace />;
  }

  // 認証済みの場合はメインレイアウトを表示
  return (
    <>
      <HeaderDemoNotice onResetDemo={user?.role === Role.ADMIN ? handleResetDemo : undefined} />
      <MasterLayout>
        <Outlet />
      </MasterLayout>

      {/* Demo Reset Confirm Dialog */}
      <DemoResetConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleConfirmReset}
        onCancel={() => setShowResetConfirm(false)}
      />

      {/* Alert Notification */}
      {alertState.show && (
        <div className='fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4'>
          <Alert
            variant={alertState.variant}
            title={alertState.title}
            dismissible
            onDismiss={hideAlert}
          >
            {alertState.message}
          </Alert>
        </div>
      )}
    </>
  );
};

const AppRoutes: React.FC = () => {
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  return (
    <HashRouter>
      <Routes>
        {isDemoMode ? (
          // デモモード時のルーティング
          <>
            <Route path='/' element={<DemoSelectionPage />} />
            <Route path='/login' element={<Navigate to='/' replace />} />
            <Route element={<ProtectedRoute />}>
              <Route path='/dashboard' element={<DashboardPage />} />
              <Route path='/profile' element={<UserProfilePage />} />
              <Route path='/master/clients' element={<PartnerListPage />} />
              <Route path='/master/land-transporters' element={<LandTransporterListPage />} />
              <Route path='/master/vehicle-models' element={<VehicleModelListPage />} />
              <Route path='/master/vehicle-types' element={<VehicleTypeListPage />} />
              <Route path='/cases' element={<CaseListPage />} />
              <Route path='/cases/new' element={<CaseFormPage />} />
              <Route path='/cases/:caseId' element={<CaseDetailPage />} />
              <Route path='/cases/:caseId/edit' element={<CaseFormPage />} />
              <Route path='/driver/cases' element={<DriverCasesPage />} />
              <Route path='/schedule' element={<SchedulePage />} />
              <Route path='/schedule/register' element={<ScheduleRegistrationPage />} />
              <Route path='/reports' element={<ReportPage />} />
              <Route path='/admin/account' element={<AccountPage />} />
              <Route path='/admin/settings' element={<SettingsPage />} />
              <Route path='/admin/users' element={<CompanyUsersPage />} />
              <Route path='/admin/super' element={<SuperAdminDashboard />} />
              <Route path='/admin/companies' element={<CompanyManagement />} />
              <Route path='/admin/company-admins' element={<CompanyAdminManagement />} />
              <Route path='/admin/company-admins/new' element={<CompanyAdminRegistration />} />
              <Route path='/admin/all-users' element={<AllUsersManagement />} />
              <Route path='/admin/users/:userId' element={<UserDetailPage />} />
              <Route path='/admin/audit-logs' element={<AuditLogsPage />} />
              <Route path='/admin/system-health' element={<SystemHealthPage />} />
              <Route path='/admin/backup-status' element={<BackupStatusPage />} />
              <Route path='/admin/business-partners' element={<BusinessPartnerMasterPage />} />
              <Route path='/admin/daily-report-logs' element={<DailyReportLogsPage />} />
              <Route path='/admin/schedules' element={<ScheduleManagementPage />} />
            </Route>
            <Route path='*' element={<Navigate to='/' />} />
          </>
        ) : (
          // 通常モード時のルーティング
          <>
            <Route path='/login' element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path='/' element={<DashboardPage />} />
              <Route path='/profile' element={<UserProfilePage />} />
              <Route path='/master/clients' element={<PartnerListPage />} />
              <Route path='/master/land-transporters' element={<LandTransporterListPage />} />
              <Route path='/master/vehicle-models' element={<VehicleModelListPage />} />
              <Route path='/master/vehicle-types' element={<VehicleTypeListPage />} />
              <Route path='/cases' element={<CaseListPage />} />
              <Route path='/cases/new' element={<CaseFormPage />} />
              <Route path='/cases/:caseId' element={<CaseDetailPage />} />
              <Route path='/cases/:caseId/edit' element={<CaseFormPage />} />
              <Route path='/driver/cases' element={<DriverCasesPage />} />
              <Route path='/schedule' element={<SchedulePage />} />
              <Route path='/schedule/register' element={<ScheduleRegistrationPage />} />
              <Route path='/reports' element={<ReportPage />} />
              <Route path='/admin/account' element={<AccountPage />} />
              <Route path='/admin/settings' element={<SettingsPage />} />
              <Route path='/admin/users' element={<CompanyUsersPage />} />
              <Route path='/admin/super' element={<SuperAdminDashboard />} />
              <Route path='/admin/companies' element={<CompanyManagement />} />
              <Route path='/admin/company-admins' element={<CompanyAdminManagement />} />
              <Route path='/admin/company-admins/new' element={<CompanyAdminRegistration />} />
              <Route path='/admin/all-users' element={<AllUsersManagement />} />
              <Route path='/admin/users/:userId' element={<UserDetailPage />} />
              <Route path='/admin/audit-logs' element={<AuditLogsPage />} />
              <Route path='/admin/system-health' element={<SystemHealthPage />} />
              <Route path='/admin/backup-status' element={<BackupStatusPage />} />
              <Route path='/admin/business-partners' element={<BusinessPartnerMasterPage />} />
              <Route path='/admin/daily-report-logs' element={<DailyReportLogsPage />} />
              <Route path='/admin/schedules' element={<ScheduleManagementPage />} />
            </Route>
            <Route path='*' element={<Navigate to='/' />} />
          </>
        )}
      </Routes>
    </HashRouter>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DateProvider>
        <MasterDataProvider>
          <AppRoutes />
        </MasterDataProvider>
      </DateProvider>
    </AuthProvider>
  );
};

export default App;
